import React from "react";
import { ReactComponent as AlertSvg } from "./alert.svg";
import { ReactComponent as ArrowDown } from "./arrowDown.svg";
import { ReactComponent as ChangePasswordSvg } from "./changePassword.svg";
import { ReactComponent as CloseSvg } from "./close.svg";
import { ReactComponent as DisableArrowDown } from "./disableArrowDown.svg";
import { ReactComponent as EditUserSvg } from "./edit-user.svg";
import { ReactComponent as ExitSvg } from "./exit.svg";
import { ReactComponent as LogoSvg } from "./logo.svg";
import { ReactComponent as MailSvg } from "./mail.svg";
import { ReactComponent as PaginationArrowLeftSvg } from "./pagination-arrow-left.svg";
import { ReactComponent as PaginationArrowRightSvg } from "./pagination-arrow-right.svg";
import { ReactComponent as PaginationDoubleArrowLeftSvg } from "./pagination-double-arrow-left.svg";
import { ReactComponent as PaginationDoubleArrowRightSvg } from "./pagination-double-arrow-right.svg";
import { ReactComponent as PasswordDisplaySvg } from "./passwordDisplay.svg";
import { ReactComponent as PasswordHiddenSvg } from "./passwordHidden.svg";
import { ReactComponent as ProductsSvg } from "./products.svg";
import { ReactComponent as ProfileSvg } from "./profile.svg";
import { ReactComponent as SearchSvg } from "./search.svg";
import { ReactComponent as SearchNoResultsSvg } from "./search-no-results.svg";
import { ReactComponent as SortAscendingSvg } from "./sort-ascending.svg";
import { ReactComponent as SortDescendingSvg } from "./sort-descending.svg";
import { ReactComponent as TooltipSvg } from "./tooltip.svg";
import { ReactComponent as TrashSvg } from "./trash.svg";
import { ReactComponent as UploadSvg } from "./upload.svg";
import { ReactComponent as UserSvg } from "./user.svg";
import { ReactComponent as UserManagementSvg } from "./userManagement.svg";
import { ReactComponent as SpinnerSvg } from "./spinner.svg";

export interface IIconProps {
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  dataTestId?: string;
}

const createIcon =
  (svg: JSX.Element, iconClassName: string) =>
  ({ className, dataTestId, onClick }: IIconProps) => {
    const classNames = `svg-icon-holder ${iconClassName} ${className || ""}`;

    return (
      <div className={classNames} onClick={onClick} data-testid={dataTestId ?? iconClassName} role="presentation">
        {svg}
      </div>
    );
  };

export const AlertIcon = createIcon(<AlertSvg />, "alert-icon");

export const ArrowDownIcon = createIcon(<ArrowDown />, "arrow-down-icon");

export const ChangePasswordIcon = createIcon(<ChangePasswordSvg />, "change-password-icon");

export const CloseIcon = createIcon(<CloseSvg />, "close-icon");

export const DisableArrowDownIcon = createIcon(<DisableArrowDown />, "disable-arrow-down-icon");

export const EditUserIcon = createIcon(<EditUserSvg />, "edit-user-icon");

export const ExitIcon = createIcon(<ExitSvg />, "exit-icon");

export const LogoIcon = createIcon(<LogoSvg />, "logo-icon");

export const MailIcon = createIcon(<MailSvg />, "mail-icon");

export const PaginationArrowLeftIcon = createIcon(<PaginationArrowLeftSvg />, "pagination-arrow-left");

export const PaginationArrowRightIcon = createIcon(<PaginationArrowRightSvg />, "pagination-arrow-right");

export const PaginationDoubleArrowLeftIcon = createIcon(
  <PaginationDoubleArrowLeftSvg />,
  "pagination-double-arrow-left",
);

export const PaginationDoubleArrowRightIcon = createIcon(
  <PaginationDoubleArrowRightSvg />,
  "pagination-double-arrow-right",
);

export const PasswordDisplayIcon = createIcon(<PasswordDisplaySvg />, "password-display-icon");

export const PasswordHiddenIcon = createIcon(<PasswordHiddenSvg />, "password-hidden-icon");

export const ProductsIcon = createIcon(<ProductsSvg />, "products-icon");

export const ProfileIcon = createIcon(<ProfileSvg />, "profile-icon");

export const SearchIcon = createIcon(<SearchSvg />, "search-icon");

export const SearchNoResultsIcon = createIcon(<SearchNoResultsSvg />, "search-no-results-icon");

export const SortAscendingIcon = createIcon(<SortAscendingSvg />, "sort-ascending");

export const SortDescendingIcon = createIcon(<SortDescendingSvg />, "sort-descending");

export const TooltipIcon = createIcon(<TooltipSvg />, "tooltip-icon");

export const TrashIcon = createIcon(<TrashSvg />, "trash-icon");

export const UploadIcon = createIcon(<UploadSvg />, "upload-icon");

export const UserIcon = createIcon(<UserSvg />, "user-icon");

export const UserManagementIcon = createIcon(<UserManagementSvg />, "user-management-icon");

export const Spinner = createIcon(<SpinnerSvg />, "spinner");

export const LogoWithNameIcon = ({
  className,
  dataTestId,
}: IIconProps) => {
  const brandLogo = process.env.REACT_APP_BRAND_LOGO;

  const resolveLogo = () => {
    if (!brandLogo) {
      return "/logos/default.svg";
    }
    if (/^https?:\/\//i.test(brandLogo)) {
      return brandLogo;
    }
    if (brandLogo.startsWith("/")) {
      return brandLogo;
    }
    return `/logos/${brandLogo.toLowerCase()}.svg`;
  };

  const logoSrc = resolveLogo();

  return (
    <div
      className={`svg-icon-holder logo-with-name-icon ${className ?? ""}`}
      data-testid={dataTestId ?? "logo-with-name-icon"}
    >
      <img src={logoSrc} alt="Brand Logo" className="brand-logo" />
    </div>
  );
};